/**
 * @license Copyright (c) 2014-2024, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

import { ClassicEditor } from "@ckeditor/ckeditor5-editor-classic";

// Import des styles personnalisés pour CodeBlock
import './codeblock-styles.css';

import { Alignment } from "@ckeditor/ckeditor5-alignment";
import { Autoformat } from "@ckeditor/ckeditor5-autoformat";
import {
  Bold,
  Code,
  Italic,
  Strikethrough,
  Subscript,
  Superscript,
  Underline,
} from "@ckeditor/ckeditor5-basic-styles";
import { BlockQuote } from "@ckeditor/ckeditor5-block-quote";
import { CodeBlock } from "@ckeditor/ckeditor5-code-block";
import type { EditorConfig } from "@ckeditor/ckeditor5-core";
import { Essentials } from "@ckeditor/ckeditor5-essentials";
import {
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
} from "@ckeditor/ckeditor5-font";
import { Heading } from "@ckeditor/ckeditor5-heading";
import { Highlight } from "@ckeditor/ckeditor5-highlight";
import { HorizontalLine } from "@ckeditor/ckeditor5-horizontal-line";
import { GeneralHtmlSupport } from "@ckeditor/ckeditor5-html-support";
import {
  AutoImage,
  Image,
  ImageCaption,
  ImageResize,
  ImageStyle,
  ImageToolbar,
  ImageUpload,
} from "@ckeditor/ckeditor5-image";
import { Indent, IndentBlock } from "@ckeditor/ckeditor5-indent";
import { TextPartLanguage } from '@ckeditor/ckeditor5-language';
import { Link, LinkImage } from "@ckeditor/ckeditor5-link";
import { List, TodoList } from "@ckeditor/ckeditor5-list";
import { FindAndReplace } from "@ckeditor/ckeditor5-find-and-replace";
import { MediaEmbed } from "@ckeditor/ckeditor5-media-embed";
import { Mention } from "@ckeditor/ckeditor5-mention";
import { PageBreak } from "@ckeditor/ckeditor5-page-break";
import { Paragraph } from "@ckeditor/ckeditor5-paragraph";
import { PasteFromOffice } from "@ckeditor/ckeditor5-paste-from-office";
import { RemoveFormat } from "@ckeditor/ckeditor5-remove-format";
import { ShowBlocks } from "@ckeditor/ckeditor5-show-blocks";
import { SelectAll } from "@ckeditor/ckeditor5-select-all";
import {
  SpecialCharacters,
  SpecialCharactersArrows,
  SpecialCharactersCurrency,
  SpecialCharactersEssentials,
  SpecialCharactersLatin,
  SpecialCharactersMathematical,
  SpecialCharactersText,
} from "@ckeditor/ckeditor5-special-characters";
import { Style } from "@ckeditor/ckeditor5-style";
import {
  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableProperties,
  TableToolbar,
} from "@ckeditor/ckeditor5-table";
import { TextTransformation } from "@ckeditor/ckeditor5-typing";
import { Undo } from "@ckeditor/ckeditor5-undo";
import { WordCount } from "@ckeditor/ckeditor5-word-count";
import { SimpleUploadAdapter } from "@ckeditor/ckeditor5-upload";
import Abbreviation from "./abbreviation/abbreviation";
import SimpleBox from "./highlight-text/highlight-text";
import SimpleBox1 from "./highlight-text-1/highlight-text";
import BoxDropdown from "./box-list/box_list";
import CustomHeadingSelector from "./custom-heading-selector/custom-heading-selector";
import ImageBrowser from "./image-browser/image-browser";

const customColorPalette = [
  // Couleurs de base (comme Word)
  { color: "hsl(0, 0%, 0%)", label: "Black" },
  { color: "hsl(0, 0%, 30%)", label: "Dark Gray" },
  { color: "hsl(0, 0%, 60%)", label: "Gray" },
  { color: "hsl(0, 0%, 90%)", label: "Light Gray" },
  { color: "hsl(0, 0%, 100%)", label: "White" },
  
  // Couleurs thématiques
  { color: "hsl(4, 90%, 58%)", label: "Red" },
  { color: "hsl(340, 82%, 52%)", label: "Pink" },
  { color: "hsl(291, 64%, 42%)", label: "Purple" },
  { color: "hsl(262, 52%, 47%)", label: "Deep Purple" },
  { color: "hsl(231, 48%, 48%)", label: "Indigo" },
  { color: "hsl(207, 90%, 54%)", label: "Blue" },
  { color: "hsl(199, 98%, 48%)", label: "Light Blue" },
  { color: "hsl(187, 100%, 42%)", label: "Cyan" },
  { color: "hsl(174, 100%, 29%)", label: "Teal" },
  { color: "hsl(122, 39%, 49%)", label: "Green" },
  { color: "hsl(88, 50%, 53%)", label: "Light Green" },
  { color: "hsl(66, 70%, 54%)", label: "Lime" },
  { color: "hsl(54, 100%, 62%)", label: "Yellow" },
  { color: "hsl(45, 100%, 51%)", label: "Amber" },
  { color: "hsl(36, 100%, 50%)", label: "Orange" },
  { color: "hsl(14, 100%, 57%)", label: "Deep Orange" },
  { color: "hsl(16, 25%, 38%)", label: "Brown" }
];

// You can read more about extending the build with additional plugins in the "Installing plugins" guide.
// See https://ckeditor.com/docs/ckeditor5/latest/installation/plugins/installing-plugins.html for details.

class Editor extends ClassicEditor {
  public static override builtinPlugins = [
    Alignment,
    AutoImage,
    Autoformat,
    BlockQuote,
    Bold,
    Code,
    CodeBlock,
    Essentials,
    FindAndReplace, // Rechercher/Remplacer
    FontBackgroundColor,
    FontColor,
    FontFamily,
    FontSize,
    GeneralHtmlSupport,
    Heading,
    Highlight,
    HorizontalLine,
    Image,
    ImageCaption,
    ImageResize,
    ImageStyle,
    ImageToolbar,
    ImageUpload,
    Indent,
    IndentBlock,
    Italic,
    Link,
    LinkImage,
    List,
    MediaEmbed,
    Mention,
    PageBreak,
    Paragraph,
    PasteFromOffice,
    RemoveFormat,
    SelectAll,
    ShowBlocks,
    SimpleUploadAdapter,
    SpecialCharacters,
    SpecialCharactersArrows,
    SpecialCharactersCurrency,
    SpecialCharactersEssentials,
    SpecialCharactersLatin,
    SpecialCharactersMathematical,
    SpecialCharactersText,
    Style,
    Subscript,
    Table,
    TableCaption,
    TableCellProperties,
    TableColumnResize,
    TableProperties,
    TableToolbar,
    TextTransformation,
    TextPartLanguage,
    TodoList,
    Underline,
    Undo,
    WordCount,
    Strikethrough,
    Superscript,
    // Custom plugins
    SimpleBox,
    SimpleBox1,
    BoxDropdown,
    CustomHeadingSelector,
    ImageBrowser,
  ];

  public static override defaultConfig: EditorConfig = {
    toolbar: {
      items: [
        // Formatage principal
        "undo", "redo", "|",
        "fontFamily", "fontSize", "|",
        "bold", "italic", "underline", "strikethrough", "|",
        "fontColor", "fontBackgroundColor", "|",
        "alignment", "|",
        
        // Structure et navigation
        "heading", "customHeadingSelector", "|",
        "bulletedList", "numberedList", "|",
        "outdent", "indent", "|",
        "link", "imageUpload", "imageBrowser", "insertTable", "|",
        "findAndReplace", "|",
        
        // Fonctionnalités avancées
        "highlight", "blockQuote", "|",
        "subscript", "superscript", "|",
        "specialCharacters", "horizontalLine", "pageBreak", "|",
        "code", "codeBlock", "|",
        "mediaEmbed", "|",
        "removeFormat", "|",
        "selectAll", "showBlocks", "|",
        "boxDropdown"
      ],
      shouldNotGroupWhenFull: true, // Éviter le groupement pour éviter les conflits avec les sauts de ligne
    },
    // Configuration des polices (comme Word)
    fontFamily: {
      options: [
        'default',
        'Arial, Helvetica, sans-serif',
        'Courier New, Courier, monospace',
        'Georgia, serif',
        'Lucida Sans Unicode, Lucida Grande, sans-serif',
        'Tahoma, Geneva, sans-serif',
        'Times New Roman, Times, serif',
        'Trebuchet MS, Helvetica, sans-serif',
        'Verdana, Geneva, sans-serif',
        'Calibri, sans-serif',
        'Cambria, serif'
      ],
      supportAllValues: true
    },
    fontSize: {
      options: [8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 36, 48, 72],
      supportAllValues: true
    },
    // Configuration des couleurs
    fontColor: {
      colors: customColorPalette,
      documentColors: 10,
      colorPicker: {
        format: 'hex'
      }
    },
    fontBackgroundColor: {
      colors: customColorPalette,
      documentColors: 10,
      colorPicker: {
        format: 'hex'
      }
    },
    language: {
      ui: "en",
      content: "en",
      textPartLanguage: [
          { title: 'Arabic', languageCode: 'ar' },
          { title: 'French', languageCode: 'fr' },
          { title: 'Hebrew', languageCode: 'he' },
          { title: 'Spanish', languageCode: 'es' }
      ]
  },
    list: {
      properties: {
        styles: true,
        startIndex: true,
        reversed: true,
      },
    },
    image: {
      toolbar: [
        "imageTextAlternative",
        "toggleImageCaption",
        "imageStyle:inline",
        "imageStyle:block",
        "imageStyle:side",
        "linkImage",
        "imageResize"
      ],
      // Configuration des styles d'images
      styles: {
        // Configuration des options de style d'image
        options: [
          'inline',
          'alignLeft',
          'alignRight',
          'alignCenter',
          'alignBlockLeft',
          'alignBlockRight',
          'block',
          'side'
        ]
      },
      // Configuration du redimensionnement
      resizeOptions: [
        {
          name: 'resizeImage:original',
          label: 'Taille originale',
          value: null
        },
        {
          name: 'resizeImage:25',
          label: '25%',
          value: '25'
        },
        {
          name: 'resizeImage:50',
          label: '50%',
          value: '50'
        },
        {
          name: 'resizeImage:75',
          label: '75%',
          value: '75'
        }
      ]
    },
    table: {
      contentToolbar: [
        "tableColumn",
        "tableRow",
        "mergeTableCells",
        "tableCellProperties",
        "tableProperties",
        'toggleTableCaption',
      ],
      // Set the palettes for tables.
      tableProperties: {
        borderColors: customColorPalette,
        backgroundColors: customColorPalette,
      },

      // Set the palettes for table cells.
      tableCellProperties: {
        borderColors: customColorPalette,
        backgroundColors: customColorPalette,
      },
    },
    simpleUpload: {
      // The URL that the images are uploaded to.
      uploadUrl:
        "https://fast-api-test-1.herokuapp.com/api/fr/back-office/ckeditor-upload-image",

      // Enable the XMLHttpRequest.withCredentials property.
      withCredentials: false,
    },
    // Configuration complète pour CodeBlock avec coloration syntaxique
    codeBlock: {
      languages: [
        // Langages les plus populaires
        { language: 'plaintext', label: 'Plain text', class: 'language-text' },
        { language: 'javascript', label: 'JavaScript', class: 'language-js' },
        { language: 'typescript', label: 'TypeScript', class: 'language-ts' },
        { language: 'python', label: 'Python', class: 'language-python' },
        { language: 'java', label: 'Java', class: 'language-java' },
        { language: 'csharp', label: 'C#', class: 'language-cs' },
        { language: 'cpp', label: 'C++', class: 'language-cpp' },
        { language: 'c', label: 'C', class: 'language-c' },
        { language: 'php', label: 'PHP', class: 'language-php' },
        { language: 'ruby', label: 'Ruby', class: 'language-ruby' },
        { language: 'go', label: 'Go', class: 'language-go' },
        { language: 'rust', label: 'Rust', class: 'language-rust' },
        { language: 'swift', label: 'Swift', class: 'language-swift' },
        { language: 'kotlin', label: 'Kotlin', class: 'language-kotlin' },
        
        // Langages web
        { language: 'html', label: 'HTML', class: 'language-html' },
        { language: 'css', label: 'CSS', class: 'language-css' },
        { language: 'scss', label: 'SCSS', class: 'language-scss' },
        { language: 'sass', label: 'Sass', class: 'language-sass' },
        { language: 'less', label: 'Less', class: 'language-less' },
        
        // Frameworks et bibliothèques
        { language: 'xml', label: 'XML', class: 'language-xml' },
        { language: 'json', label: 'JSON', class: 'language-json' },
        { language: 'yaml', label: 'YAML', class: 'language-yaml' },
        { language: 'markdown', label: 'Markdown', class: 'language-md' },
        
        // Bases de données
        { language: 'sql', label: 'SQL', class: 'language-sql' },
        { language: 'mysql', label: 'MySQL', class: 'language-mysql' },
        { language: 'postgresql', label: 'PostgreSQL', class: 'language-postgresql' },
        
        // Outils et configuration
        { language: 'bash', label: 'Bash', class: 'language-bash' },
        { language: 'shell', label: 'Shell', class: 'language-shell' },
        { language: 'powershell', label: 'PowerShell', class: 'language-powershell' },
        { language: 'dockerfile', label: 'Dockerfile', class: 'language-dockerfile' },
        { language: 'nginx', label: 'Nginx', class: 'language-nginx' },
        { language: 'apache', label: 'Apache', class: 'language-apache' },
        
        // Autres langages populaires
        { language: 'r', label: 'R', class: 'language-r' },
        { language: 'matlab', label: 'MATLAB', class: 'language-matlab' },
        { language: 'perl', label: 'Perl', class: 'language-perl' },
        { language: 'lua', label: 'Lua', class: 'language-lua' },
        { language: 'scala', label: 'Scala', class: 'language-scala' },
        { language: 'clojure', label: 'Clojure', class: 'language-clojure' },
        { language: 'haskell', label: 'Haskell', class: 'language-haskell' },
        { language: 'erlang', label: 'Erlang', class: 'language-erlang' },
        { language: 'elixir', label: 'Elixir', class: 'language-elixir' },
        
        // Langages de template
        { language: 'twig', label: 'Twig', class: 'language-twig' },
        { language: 'handlebars', label: 'Handlebars', class: 'language-handlebars' },
        { language: 'django', label: 'Django Template', class: 'language-django' },
        { language: 'smarty', label: 'Smarty', class: 'language-smarty' }
      ],
      indentSequence: '  ' // 2 espaces pour l'indentation
    },
    heading: {
      options: [
        {
          model: "paragraph",
          title: "Paragraph",
          class: "ck-heading_paragraph",
        },
        {
          model: "heading1",
          view: "h1",
          title: "Heading 1",
          class: "ck-heading_heading1",
        },
        {
          model: "heading2",
          view: "h2",
          title: "Heading 2",
          class: "ck-heading_heading2",
        },
        {
          model: "heading3",
          view: "h3",
          title: "Heading 3",
          class: "ck-heading_heading3",
        },
        {
          model: "heading4",
          view: "h4",
          title: "Heading 4",
          class: "ck-heading_heading4",
        },
        {
          model: "heading5",
          view: "h5",
          title: "Heading 5",
          class: "ck-heading_heading5",
        },
        {
          model: "heading6",
          view: "h6",
          title: "Heading 6",
          class: "ck-heading_heading6",
        },
      ],
    },
  };
}

export default Editor;
